import { message, Spin, Upload, UploadFile, UploadProps } from "antd";
import { useContext, useEffect, useRef, useState } from "react";
import { useDebounce } from "ahooks";
import styles from "./reportUpload.module.css";
import { UploadRef } from "antd/lib/upload/Upload";
import { AdminContext } from "context/adminContext";
import { BunchFilesType, FileDataType } from "./ModalReport";
import { CustomButton } from "Components/CustomButton/CustomButton";
import { PaperClipIcon } from "Common/Icons/PaperClip";
import trashRedIcon from "assets/svg/TrashRed.svg";
import { ReportSectionKey, uploadDebateReportPhoto } from "helpers/backend_helper";

const MAX_UPLOAD_PHOTOS = 6;
const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE = 1024 * 1024 * MAX_FILE_SIZE_MB;

type Props = {
  debateId: string;
  section: ReportSectionKey;
  fileList: UploadFile[];
  setFileList: React.Dispatch<React.SetStateAction<UploadFile[]>>;
  setBunchFiles: React.Dispatch<React.SetStateAction<BunchFilesType>>;
  loading?: boolean;
};

export const ReportUpload = ({ debateId, section, fileList, setFileList, setBunchFiles, loading }: Props) => {
  const [isAlotFiles, setIsAlotFiles] = useState(false);
  const [isBigFiles, setIsBigFiles] = useState(false);
  const isBigFilesMB = useDebounce(isBigFiles, { wait: 100 });
  const { localCountry } = useContext(AdminContext);

  const isMoreThanAmount = useDebounce(isAlotFiles, { wait: 100 });

  useEffect(() => {
    if (!fileList.length) {
      setBunchFiles({ files: [], status: "READY" });
    }
  }, [fileList, setBunchFiles]);
  useEffect(() => {
    if (!fileList.length) return;
    const isDone = fileList.every(
      (file) => file.status === "done" || file.status === "error" || file.status === "removed",
    );
    setBunchFiles((prev) => ({ ...prev, status: isDone ? "DONE" : "LOADING" }));
  }, [fileList, setBunchFiles]);

  const uploadRequest: UploadProps["customRequest"] = async ({ file, onSuccess, onError, onProgress }) => {
    const f = file as File;
    try {
      onProgress?.({ percent: 40 });
      const res = await uploadDebateReportPhoto(debateId, f, section, localCountry?.iso?.toUpperCase() || "RU");
      onProgress?.({ percent: 100 });
      const uploaded = Array.isArray(res) ? res[0] : res;
      const fileData: FileDataType = {
        filePath: uploaded?.url ?? uploaded?.filePath ?? uploaded?.path,
        originalName: uploaded?.originalName ?? f.name,
      };
      onSuccess?.(fileData);
    } catch (err) {
      onError?.(err as Error);
    }
  };

  const uploadPropsFile: UploadProps = {
    accept: "image/png,image/jpeg,image/jpg",
    customRequest: uploadRequest,
    multiple: true,
    maxCount: MAX_UPLOAD_PHOTOS,
    listType: "picture-card",
    onChange: ({ fileList, file }) => {
      setFileList(fileList);
      if (file.status === "removed") {
        setBunchFiles((prev) => ({
          ...prev,
          files: prev.files.filter((fileToUpload) => file.name !== fileToUpload.originalName),
        }));
        setFileList((prev) => {
          return prev.filter((v) => v.name !== file.name);
        });
        return;
      }

      if (file?.response && file?.status === "done") {
        setBunchFiles((prev) => {
          const existingFile = prev.files.find((v) => v.originalName === file.response.originalName);
          if (!existingFile) {
            prev.files.push(file.response);
            return { ...prev };
          }
          return prev;
        });
      }

      if (file.status === "error") {
        setFileList((prev) => {
          return prev.filter((v) => v.name !== file.name);
        });

        message.error(file.error?.message);
        return;
      }
    },
    itemRender: (orig, file, _, action) => {
      const imgFile = file.thumbUrl || file.url;
      const isReady = file.status === "done" || file.status === "removed" || file.url;

      if (isReady && imgFile) {
        return (
          <div className={styles.itemUploadedRender}>
            <img src={imgFile} alt="picture" />

            <CustomButton
              sizeButton="icon"
              typeButton="critical"
              className={styles.trashBtn}
              onClick={action.remove.bind(action)}
            >
              <img src={trashRedIcon} alt="trash" />
            </CustomButton>
            {false && (
              <div className={styles.uploadOverlay}>
                <Spin size="large" />
              </div>
            )}
          </div>
        );
      }

      if (!imgFile && isReady) {
        return <div></div>;
      }

      return orig;
    },
    beforeUpload: (file, currentFileList) => {
      if (file.size > MAX_FILE_SIZE) {
        setIsBigFiles(true);
        return Upload.LIST_IGNORE;
      }

      const freeSlots = MAX_UPLOAD_PHOTOS - fileList.length;
      const slotIndex = currentFileList.filter((v) => v.size <= MAX_FILE_SIZE).indexOf(file);

      if (slotIndex >= freeSlots) {
        setIsAlotFiles(true);
        return Upload.LIST_IGNORE;
      }

      setBunchFiles((prev) => ({ ...prev, status: "LOADING" }));
      return true;
    },
    fileList: fileList,
  };

  useEffect(() => {
    if (isMoreThanAmount) {
      message.warning(`Maximum ${MAX_UPLOAD_PHOTOS} files allowed`);
      setIsAlotFiles(false);
    }
  }, [isMoreThanAmount]);
  useEffect(() => {
    if (isBigFilesMB) {
      message.error(`You can't load a file that more than ${MAX_FILE_SIZE_MB} MB`);
      setIsBigFiles(false);
    }
  }, [isBigFilesMB]);

  const uploadRef = useRef<UploadRef>(null);

  const openUpload = () => {
    const input = uploadRef.current?.nativeElement?.querySelector('input[type="file"]') as HTMLInputElement;

    input?.click();
  };

  return (
    <div className={styles.uploadReportProblemWrapper} style={fileList.length ? { marginTop: 10 } : null}>
      <Upload {...uploadPropsFile} className={styles["upload-report-problem"]} ref={uploadRef} showUploadList>
        <div style={{ display: "none" }} />
      </Upload>

      <div className={styles.buttonAttachWithTextWrapper}>
        <CustomButton
          sizeButton="medium"
          typeButton="tertiary"
          onClick={openUpload}
          disabled={fileList.length >= MAX_UPLOAD_PHOTOS}
          loading={loading}
        >
          <div className={styles.attachPhotoButtonWrapper}>
            <PaperClipIcon width={16} height={16} backgroundSize={16} filter="brightness(0)" />
            <span>Attach Photos</span>
          </div>
        </CustomButton>
        <span className={styles.descriptionOfTypes}>
          PNG, JPEG less {MAX_FILE_SIZE_MB}MB, max {MAX_UPLOAD_PHOTOS} files
        </span>
      </div>
    </div>
  );
};
